import { consola } from 'consola'
import { useUnreadNotifications } from './useUnreadNotifications'
import { apiNotification } from '@entities/notification'
import type { UserNotificationItem, NotificationStats } from '@entities/notification'

const logger = consola.withTag('notifications')

const PAGINATION_CONFIG = { perPage: 10, defaultPage: 1 } as const

export const useNotifications = () => {
  const globalUnread = useUnreadNotifications()

  const loading = ref(false)
  const loadingMore = ref(false)
  const notifications = ref<UserNotificationItem[]>([])
  const stats = ref<NotificationStats>({ total: 0, unread: 0, read: 0 })
  const hasMore = ref(true)
  const currentPage = ref(PAGINATION_CONFIG.defaultPage)
  const total = ref(0)
  const totalPages = ref(0)
  const unreadOnly = ref(false)

  const isEmpty = computed(() => notifications.value.length === 0)
  const isInitialLoading = computed(() => loading.value && notifications.value.length === 0)
  const canLoadMore = computed(() => hasMore.value && !loadingMore.value)
  const unreadCount = computed(() => stats.value.unread)

  const fetchNotifications = async (page = 1, resetList = true): Promise<void> => {
    if (loading.value) return
    try {
      loading.value = true
      currentPage.value = page
      const offset = (page - 1) * PAGINATION_CONFIG.perPage
      const response = await apiNotification.getNotifications({
        limit: PAGINATION_CONFIG.perPage,
        offset,
        unreadOnly: unreadOnly.value || undefined,
      })
      notifications.value = resetList
        ? response.notifications || []
        : [...notifications.value, ...response.notifications]
      stats.value = response.stats
      total.value = response.total
      totalPages.value = Math.ceil(response.total / PAGINATION_CONFIG.perPage)
      hasMore.value = notifications.value.length < response.total
    } catch (error) {
      logger.error('Ошибка при загрузке уведомлений:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  const loadMore = async (): Promise<void> => {
    if (!hasMore.value || loadingMore.value) return
    try {
      loadingMore.value = true
      const nextPage = currentPage.value + 1
      const offset = (nextPage - 1) * PAGINATION_CONFIG.perPage
      const response = await apiNotification.getNotifications({
        limit: PAGINATION_CONFIG.perPage,
        offset,
        unreadOnly: unreadOnly.value || undefined,
      })
      if (response.notifications?.length) {
        notifications.value = [...notifications.value, ...response.notifications]
        stats.value = response.stats
        total.value = response.total
        totalPages.value = Math.ceil(response.total / PAGINATION_CONFIG.perPage)
        currentPage.value = nextPage
        hasMore.value = notifications.value.length < response.total
      } else {
        hasMore.value = false
      }
    } catch (error) {
      logger.error('Ошибка при загрузке дополнительных уведомлений:', error)
      throw error
    } finally {
      loadingMore.value = false
    }
  }

  const markAsRead = async (notificationId: string): Promise<void> => {
    try {
      await apiNotification.markAsRead(notificationId)
      const notification = notifications.value.find(n => n.id === notificationId)
      if (notification && !notification.isRead) {
        notification.isRead = true
        notification.openedAt = new Date().toISOString()
        stats.value.unread = Math.max(0, stats.value.unread - 1)
        stats.value.read++
        globalUnread.decreaseUnreadCount(1)
      }
    } catch (error) {
      logger.error('Ошибка при пометке уведомления как прочитанного:', error)
      throw error
    }
  }

  const markAllAsRead = async (): Promise<void> => {
    try {
      await apiNotification.markAllAsRead()
      for (const n of notifications.value) {
        if (!n.isRead) {
          n.isRead = true
          n.openedAt = new Date().toISOString()
        }
      }
      stats.value.read = stats.value.total
      stats.value.unread = 0
      globalUnread.clearUnreadCount()
    } catch (error) {
      logger.error('Ошибка при пометке всех уведомлений как прочитанных:', error)
      throw error
    }
  }

  const toggleUnreadFilter = async (value: boolean): Promise<void> => {
    unreadOnly.value = value
    await fetchNotifications(1, true)
  }

  const initLoad = async (): Promise<void> => {
    await fetchNotifications()
  }

  const reset = (): void => {
    notifications.value = []
    stats.value = { total: 0, unread: 0, read: 0 }
    hasMore.value = true
    currentPage.value = PAGINATION_CONFIG.defaultPage
    total.value = 0
    totalPages.value = 0
    unreadOnly.value = false
    loading.value = false
    loadingMore.value = false
  }

  return {
    loading, loadingMore, notifications, stats, hasMore, currentPage, total, totalPages, unreadOnly,
    isEmpty, isInitialLoading, canLoadMore, unreadCount,
    fetchNotifications, loadMore, markAsRead, markAllAsRead, toggleUnreadFilter, initLoad, reset,
  }
}
